// ============================================================
// HEALTH RAMP - how hurt is this combatant, in one place.
//
// Four call sites each carried their own thresholds, and three of them
// disagreed: the roster chip said green while the picker said amber and
// the body map said orange, for the same wounds. One of the copies even
// carried a comment claiming they agreed.
//
// Engine-side because it is a pure data→band mapping the whole UI reads,
// and because the alternative is a fifth copy the next time someone
// needs it.
// ============================================================

import type { Target } from '../types';

export type HealthBand = 'full' | 'high' | 'med' | 'low' | 'crit' | 'unknown';

/**
 * Wounds, or magnitude for a horde - the same current-of-maximum shape.
 *
 * HERE RATHER THAN IN A COMPONENT (v8.2.4) because it is the other half
 * of `healthBand`: that function takes a current and a maximum, and this
 * is the single place that decides WHICH PAIR a given combatant's are.
 * Splitting them left the pairing enforced by convention across four
 * call sites, which is the arrangement the header above describes going
 * wrong. The battlefield needs it too, and `engine/` cannot import a
 * component.
 */
export function vitalsOf(target: Target): { shown: number; total: number; isHorde: boolean } {
  const isHorde = !!target.horde?.isHorde;
  return {
    isHorde,
    shown: isHorde ? target.horde?.magnitude || 0 : target.wounds.current,
    total: isHorde ? target.horde?.maxMagnitude || 0 : target.wounds.max,
  };
}

/** Theme variables per band. Chip colours are tokens, never raw hex. */
export const HEALTH_BAND_VAR: Record<HealthBand, string> = {
  full: 'var(--bm-health-full)',
  high: 'var(--bm-health-high)',
  med: 'var(--bm-health-med)',
  low: 'var(--bm-health-low)',
  crit: 'var(--bm-health-crit)',
  unknown: 'var(--text-dim)',
};

/**
 * Which band a combatant's health falls in.
 *
 * `full` means UNDAMAGED, not "mostly fine" - the distinction is worth a
 * band because "has this one been hit at all yet" is the question a GM
 * scanning a roster is usually asking.
 *
 * Works for wounds and for horde magnitude; both are current-of-maximum.
 */
export function healthBand(current: number, max: number): HealthBand {
  if (!(max > 0)) return 'unknown';
  const ratio = Math.max(0, current) / max;
  if (ratio >= 1) return 'full';
  if (ratio > 0.66) return 'high';
  if (ratio > 0.33) return 'med';
  if (ratio > 0) return 'low';
  return 'crit';
}

/** The CSS colour for a combatant's health. */
export function healthColor(current: number, max: number): string {
  return HEALTH_BAND_VAR[healthBand(current, max)];
}

/**
 * What a band is CALLED, for a table playing without wound numbers.
 *
 * A LABEL MAP OVER THE EXISTING RAMP, deliberately, rather than a scale
 * of its own. `healthBand` already answers this question for fourteen
 * surfaces and its header is a warning against a fifth copy - and it
 * already has the property the banding wants most: `full` means
 * UNDAMAGED, so the very first hit visibly moves the reading, which is
 * what sells chip damage as dangerous.
 *
 * A finer scale - narrower bands as wounds drop, so the change
 * accelerates and the player's last rounds feel like falling - would be
 * a better fog. It is not free, though: these five names are the five
 * COLOURS too, so a finer scale means retuning what every roster chip
 * and every token on the map is painted, for everyone, whether or not
 * they play with bands. That is a ruling about the whole app's
 * appearance, not a detail of this feature, and it deserves asking
 * about rather than smuggling in here.
 */
export const HEALTH_BAND_LABEL: Record<HealthBand, string> = {
  full: 'Unhurt',
  high: 'Grazed',
  med: 'Wounded',
  low: 'Bloodied',
  crit: 'Down',
  unknown: 'Unknown',
};

/** The band a combatant is in, named - `Bloodied`, `Down`, and so on. */
export function healthBandLabel(current: number, max: number): string {
  return HEALTH_BAND_LABEL[healthBand(current, max)];
}

/** How full a wounds bar is, as a whole percentage 0-100: below zero still reads empty, never negative. */
export function healthFill(current: number, max: number): number {
  if (!(max > 0)) return 0;
  return Math.round(Math.min(1, Math.max(0, current) / max) * 100);
}

/** Each band's fill on a gauge that may not show the number: the band's middle, and empty once down. */
const BAND_FILL: Record<HealthBand, number> = { full: 100, high: 83, med: 50, low: 17, crit: 0, unknown: 0 };

/**
 * How full a gauge is drawn (0-100): exact for a viewer who may read the numbers, else stepped by band - a smooth
 * drain on a banded table would leak the number the band hides (2026-10-26, the Object's ring and bar).
 */
export function healthGauge(current: number, max: number, showNumbers: boolean): number {
  return showNumbers ? healthFill(current, max) : BAND_FILL[healthBand(current, max)];
}

/** The amount as a viewer may read it: `7/22`, or the band's name on a table playing without numbers. */
export function healthAmount(current: number, max: number, showNumbers: boolean): string {
  return showNumbers ? `${current}/${max}` : healthBandLabel(current, max);
}
